import { useMemo, useState } from 'react';
import type { JourneyNode, NodeType } from '../../../core/entities/node';
import { useLocale } from '../../preferences/i18n/LocaleProvider';
import { FuseSearchRepository } from '../../../storage/implementations/fuse-search-repository';

type SearchResult = {
  node: JourneyNode;
  exact: boolean;
  reason: 'recent' | 'exact' | 'nearMatch';
};

export function SearchOverlay({
  nodes,
  onFocus,
  visibleType,
  onRevealAndFocus,
}: {
  nodes: JourneyNode[];
  onFocus: (nodeId: string) => void;
  visibleType?: NodeType | 'All';
  onRevealAndFocus?: (nodeId: string, nodeType: NodeType) => void;
}) {
  const [query, setQuery] = useState('');
  const { t } = useLocale();
  const hasQuery = query.trim().length > 0;
  const results = useMemo<SearchResult[]>(() => {
    const q = query.trim().toLocaleLowerCase();
    if (!q) {
      return nodes.slice(0, 5).map((node) => ({ node, exact: false, reason: 'recent' as const }));
    }

    const exact: SearchResult[] = nodes
      .filter((node) => node.displayName.toLocaleLowerCase() === q)
      .map((node) => ({ node, exact: true, reason: 'exact' as const }));

    const near: SearchResult[] = nodes
      .filter(
        (node) =>
          node.displayName.toLocaleLowerCase().includes(q) ||
          node.tags.some((tag) => tag.toLocaleLowerCase().includes(q)),
      )
      .filter((item) => !exact.some((result) => result.node.nodeId === item.nodeId))
      .slice(0, 5)
      .map((node) => ({ node, exact: false, reason: 'nearMatch' as const }));

    return exact.concat(near).slice(0, exact.length ? 10 : 5);
  }, [nodes, query]);
  void FuseSearchRepository;

  function handleSelectResult(node: JourneyNode) {
    if (onRevealAndFocus && visibleType && visibleType !== 'All' && visibleType !== node.type) {
      onRevealAndFocus(node.nodeId, node.type);
    } else {
      onFocus(node.nodeId);
    }
  }

  return (
    <section aria-label="Search overlay" className="hud-stack" style={{ padding: 12, minWidth: 220 }}>
      <label className="sr-only" htmlFor="node-search">{t('search')}</label>
      <input
        className="hud-input"
        id="node-search"
        placeholder={t('search')}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      {hasQuery ? (
        <>
          <p className="hud-hint">
            {results.some((r) => r.exact) ? t('searchJump') : t('noExact')}
          </p>
          <ul className="hud-results">
            {results.map(({ node, reason }) => (
              <li key={node.nodeId}>
                <button className="hud-result-button" onClick={() => handleSelectResult(node)}>
                  {node.displayName} <small>{t(reason)}</small>
                  {visibleType && visibleType !== 'All' && visibleType !== node.type ? (
                    <small style={{ color: 'rgba(255, 213, 156, .82)', marginLeft: 6 }}>
                      ({t('hiddenByFilter')})
                    </small>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
          <button className="hud-subtle-button" onClick={() => setQuery('')}>
            {t('clear')}
          </button>
        </>
      ) : (
        <p className="hud-hint">{t('searchHint')}</p>
      )}
    </section>
  );
}
